import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { itemAPI } from '../services/api';
import Seo, { jsonLd } from '../components/Seo';
import PublicLayout from '../components/PublicLayout';
import ProductCardMedia from '../components/ProductCardMedia';
import { ProductListSkeleton, EmptyState, ErrorState } from '../components/skeletons';

/**
 * /catalog — flat browse of every item, paginated (PAGE_SIZE per page) with
 * server-driven facet counts so chip labels stay accurate regardless of how
 * many pages have been loaded. Filter state lives in the URL:
 *   /catalog?cat=electronics&sub=mobile-phones&brand=apple&search=pro
 */

const PAGE_SIZE = 24;

const C = {
  green: '#1B5C3A', greenDark: '#0F3D26', greenLight: '#E8F5EE',
  gold: '#F5A623', goldLight: '#FFF8E7', goldDark: '#C4831A',
  cream: '#FAF8F3', charcoal: '#1A1A1A', muted: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF',
};
const F = { display: "'Fraunces', Georgia, serif", body: "'Plus Jakarta Sans', sans-serif" };

export default function Catalog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loadingItems, setLoadingItems] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [facets, setFacets] = useState({ categories: [], subCategories: [], brands: [], total: 0 });

  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');

  // Debounce the search input update to searchParams
  useEffect(() => {
    const handler = setTimeout(() => {
      const currentSearch = searchParams.get('search') || '';
      const nextVal = searchInput.trim();
      if (nextVal !== currentSearch.trim()) {
        const next = new URLSearchParams(searchParams);
        if (nextVal) next.set('search', nextVal); else next.delete('search');
        setSearchParams(next, { replace: true });
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(handler);
  }, [searchInput, searchParams, setSearchParams]);

  // Sync searchInput with URL only if the URL search query has changed from outside (e.g. back/forward button, clear filters)
  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (urlSearch !== searchInput.trim()) {
      setSearchInput(urlSearch);
    }
  }, [searchParams]);

  const searchQ = (searchParams.get('search') || '').trim();
  const catFilter = searchParams.get('cat') || '';
  const subFilter = searchParams.get('sub') || '';
  const brandFilter = searchParams.get('brand') || '';

  // Whole-filter-section collapse — session-scoped.
  const [filterSectionOpen, setFilterSectionOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const v = window.sessionStorage.getItem('kj_catalog_filters_open');
    return v === null ? true : v === '1';
  });
  const toggleFilterSection = () => {
    setFilterSectionOpen(v => {
      const next = !v;
      try { window.sessionStorage.setItem('kj_catalog_filters_open', next ? '1' : '0'); } catch { }
      return next;
    });
  };

  // Look up server IDs for the active slug-based chips so we can query items.
  const resolveId = useCallback((pool, slugOrId) => {
    if (!slugOrId) return null;
    const m = pool.find(f => (f.slug || f._id) === slugOrId);
    return m?._id || null;
  }, []);

  const buildFilterIds = useCallback(() => {
    const out = {};
    if (catFilter) out.categoryId = resolveId(facets.categories, catFilter);
    if (subFilter) out.subCategoryId = resolveId(facets.subCategories, subFilter);
    if (brandFilter) out.brandId = resolveId(facets.brands, brandFilter);
    if (searchQ) out.search = searchQ;
    // Remove nulls (id not yet resolved — happens on first render before
    // facets have loaded).
    return Object.fromEntries(Object.entries(out).filter(([, v]) => v));
  }, [catFilter, subFilter, brandFilter, searchQ, facets, resolveId]);

  // ── Refetch facets whenever filters change ───────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (searchQ) params.search = searchQ;
    // Use whatever IDs are already resolvable so facet counts narrow
    // correctly even on first paint.
    if (catFilter) params.categoryId = resolveId(facets.categories, catFilter);
    if (subFilter) params.subCategoryId = resolveId(facets.subCategories, subFilter);
    if (brandFilter) params.brandId = resolveId(facets.brands, brandFilter);
    Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });

    itemAPI.facets(params)
      .then(r => { if (!cancelled) setFacets(r.data.data || { categories: [], subCategories: [], brands: [], total: 0 }); })
      .catch(() => { });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, catFilter, subFilter, brandFilter]);

  // ── Load first page on filter / search change ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadingItems(true);
    setHasError(false);
    setPage(1);
    const filters = buildFilterIds();
    itemAPI.list({ ...filters, page: 1, limit: PAGE_SIZE })
      .then(r => {
        if (cancelled) return;
        setItems(r.data.data || []);
        setTotal(r.data.total || 0);
      })
      .catch(() => { if (!cancelled) { setItems([]); setTotal(0); setHasError(true); } })
      .finally(() => { if (!cancelled) setLoadingItems(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQ, catFilter, subFilter, brandFilter, facets]);

  const loadMore = async () => {
    if (loadingMore || loadingItems) return;
    const nextPage = page + 1;
    const filters = buildFilterIds();
    setLoadingMore(true);
    try {
      const r = await itemAPI.list({ ...filters, page: nextPage, limit: PAGE_SIZE });
      setItems(prev => [...prev, ...(r.data.data || [])]);
      setTotal(r.data.total || total);
      setPage(nextPage);
    } catch { }
    setLoadingMore(false);
  };

  const setParam = (key, value, resetDownstream = false) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (resetDownstream) {
      if (key === 'cat') { next.delete('sub'); next.delete('brand'); }
      if (key === 'sub') { next.delete('brand'); }
    }
    setSearchParams(next, { replace: true });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setParam('search', searchInput.trim());
  };

  const clearAll = () => { setSearchInput(''); setSearchParams(new URLSearchParams(), { replace: true }); };
  const hasAnyFilter = catFilter || subFilter || brandFilter || !!searchQ;
  const hasMore = items.length < total;

  return (
    <PublicLayout>
      <Seo
        title="Catalog — browse products and services"
        description="Browse every category and sub-category on Kanjusee. Pick an item, post a demand at your budget, and let verified Indian vendors compete with their best offers."
        canonical="/catalog"
        jsonLd={[jsonLd.itemList(items, 'Kanjusee catalog')]}
      />
      <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 64px)' }}>
        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '20px 24px 0' }} className="p-mobile-20">
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, maxWidth: 600, flexWrap: 'wrap' }} role="search" aria-label="Search products and services">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search products & services..."
              type="search"
              style={{ flex: '1 1 300px', fontSize: 14, padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 9, fontFamily: 'var(--font-sans)', outline: 'none', background: '#fff' }}
            />
            <button type="submit" style={{ flex: '1 1 auto', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 9, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>Search</button>
          </form>
        </div>

        <div style={{ maxWidth: 1140, margin: '0 auto', padding: '16px 24px' }} className="p-mobile-20">
          <section aria-label="Filters" style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={toggleFilterSection}
                aria-expanded={filterSectionOpen}
                aria-controls="kj-catalog-filter-body"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, color: 'var(--char)' }}
              >
                <span style={{ display: 'inline-block', transform: filterSectionOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: 'var(--muted)' }}>▸</span>
                Filters
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>
                  · {total} item{total !== 1 ? 's' : ''}{hasAnyFilter && <> matching</>}
                </span>
              </button>
              {hasAnyFilter && (
                <button onClick={clearAll} style={{ background: 'none', border: 'none', color: 'var(--green)', fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  Clear all
                </button>
              )}
            </div>

            {filterSectionOpen && (
              <div id="kj-catalog-filter-body" style={{ marginTop: 14 }}>
                <FilterRow
                  label="Category"
                  facets={facets.categories}
                  activeKey={catFilter}
                  onPick={(k) => setParam('cat', k, true)}
                />
                {facets.subCategories.length > 0 && (
                  <FilterRow
                    label="Sub-category"
                    facets={facets.subCategories}
                    activeKey={subFilter}
                    onPick={(k) => setParam('sub', k, true)}
                  />
                )}
                {facets.brands.length > 0 && (
                  <FilterRow
                    label="Brand"
                    facets={facets.brands}
                    activeKey={brandFilter}
                    onPick={(k) => setParam('brand', k)}
                  />
                )}
                {searchQ && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                    Search: "<em style={{ fontStyle: 'normal', color: 'var(--char)' }}>{searchQ}</em>"
                  </div>
                )}
              </div>
            )}
          </section>

          {loadingItems ? (
            <ProductListSkeleton count={6} columns={3} />
          ) : hasError ? (
            <ErrorState onRetry={() => { setLoadingItems(true); setHasError(false); setPage(1); const filters = buildFilterIds(); itemAPI.list({ ...filters, page: 1, limit: PAGE_SIZE }).then(r => { setItems(r.data.data || []); setTotal(r.data.total || 0); }).catch(() => setHasError(true)).finally(() => setLoadingItems(false)); }} />
          ) : items.length === 0 ? (
            <EmptyState
              title="No items found"
              description="Try a different search term or clear your applied category and brand filters."
              actionLabel="Clear Filters"
              onAction={() => setSearchParams(new URLSearchParams(), { replace: true })}
            />
          ) : (
            <>
              <div className="grid-1-2-3" style={{ gap: 20 }}>
                {items.map(item => (
                  <div key={item._id}
                    onClick={() => window.location.href = `/item/${item.slug || item._id}`}
                    style={{
                      background: '#fff', borderRadius: 14, border: '1.5px solid var(--border)', overflow: 'hidden',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.04)', transition: 'box-shadow 0.2s, border-color 0.2s',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--green)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(27,92,58,.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}>
                    {/* Category / Featured tag strip */}
                    <div style={{ background: 'var(--green-lt)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ background: 'var(--green)', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{item.categoryId?.name}</span>
                        {item.subCategoryId && <span style={{ background: 'var(--gold-lt)', color: 'var(--gold-dk)', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{item.subCategoryId.name}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                        {item.videos?.length > 0 && (
                          <span title={`${item.videos.length} video(s)`} style={{ fontSize: 10, fontWeight: 700, color: '#0369a1', background: '#e0f2fe', padding: '2px 7px', borderRadius: 10 }}>🎬 {item.videos.length}</span>
                        )}
                        {item.isFeatured && <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', background: 'var(--gold-lt)', padding: '2px 8px', borderRadius: 10 }}>⭐ Featured</span>}
                      </div>
                    </div>

                    {/* Product image */}
                    <ProductCardMedia item={item} height={180} />


                    {/* Card body */}
                    <div style={{ padding: '16px 16px 14px' }}>
                      {item.brandId && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 4, letterSpacing: 0.5 }}>{item.brandId.name}</div>}
                      <div style={{ fontFamily: F.body, fontSize: 15, fontWeight: 700, color: C.charcoal, marginBottom: 12, lineHeight: 1.35, minHeight: 40 }}>{item.name}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Link to={`/item/${item.slug || item._id}`} onClick={e => e.stopPropagation()} style={{ flex: 1, textAlign: 'center', textDecoration: 'none', background: 'transparent', color: 'var(--green)', border: '1.5px solid var(--green)', borderRadius: 9, padding: '9px 12px', fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 700 }}>Details</Link>
                        <button onClick={e => { e.stopPropagation(); navigate(`/post-demand?itemId=${item._id}`); }} style={{ flex: 1, background: 'var(--gold)', color: 'var(--char)', border: 'none', borderRadius: 9, padding: '9px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-sans)', transition: 'background 0.15s' }}>
                          Name Price →
                        </button>
                      </div>
                      {item.demandCount > 0 && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 10, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                          🔥 {item.demandCount} demand{item.demandCount > 1 ? 's' : ''} posted
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center', padding: '28px 0 16px', fontSize: 13, color: 'var(--muted)' }}>
                Showing {items.length} of {total}
              </div>
              {hasMore && (
                <div style={{ textAlign: 'center', paddingBottom: 32 }}>
                  <button
                    onClick={loadMore}
                    disabled={loadingMore}
                    style={{
                      background: 'var(--green)', color: '#fff', border: 'none',
                      borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700,
                      cursor: loadingMore ? 'wait' : 'pointer', opacity: loadingMore ? 0.6 : 1,
                    }}
                  >
                    {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, total - items.length)} more`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}

const COLLAPSE_AFTER = 6;
function FilterRow({ label, facets, activeKey, onPick }) {
  const [expanded, setExpanded] = useState(false);
  const canCollapse = facets.length > COLLAPSE_AFTER;

  let visible = facets;
  if (canCollapse && !expanded) {
    const activeIdx = facets.findIndex(f => (f.slug || f._id) === activeKey);
    if (activeIdx >= COLLAPSE_AFTER) {
      visible = [facets[activeIdx], ...facets.slice(0, COLLAPSE_AFTER - 1)];
    } else {
      visible = facets.slice(0, COLLAPSE_AFTER);
    }
  }
  const hiddenCount = facets.length - visible.length;

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {label} <span style={{ opacity: 0.6, fontWeight: 500 }}>({facets.length})</span>
        </div>
        {canCollapse && (
          <button
            onClick={() => setExpanded(e => !e)}
            aria-expanded={expanded}
            style={{ background: 'none', border: 'none', color: 'var(--green)', fontSize: 11, fontWeight: 700, cursor: 'pointer', padding: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {expanded ? '– Collapse' : '+ Expand'}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Chip active={!activeKey} onPick={() => onPick('')}>All</Chip>
        {visible.map(f => {
          const k = f.slug || f._id;
          return (
            <Chip key={k} active={activeKey === k} onPick={() => onPick(k)}>
              {f.name} <span style={{ opacity: 0.6, marginLeft: 4, fontWeight: 500 }}>({f.count})</span>
            </Chip>
          );
        })}
        {canCollapse && !expanded && hiddenCount > 0 && (
          <button
            onClick={() => setExpanded(true)}
            style={{
              padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
              border: '1.5px dashed var(--border)', background: 'transparent', color: 'var(--green)',
            }}
          >
            +{hiddenCount} more
          </button>
        )}
      </div>
    </div>
  );
}

function Chip({ active, onPick, children }) {
  return (
    <button
      onClick={onPick}
      style={{
        padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 700,
        cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
        border: active ? 'none' : '1.5px solid var(--border)',
        background: active ? 'var(--green)' : '#fff',
        color: active ? '#fff' : 'var(--char)',
      }}
    >
      {children}
    </button>
  );
}
